// Discovery provenance — which paper (seed or otherwise) led us to a paper.
// The server is the source of truth (services/discovery_provenance.py);
// these helpers tag outgoing trawl/snowball results and read links back.

import type { DiscoveryLink, DiscoveryMethod, DiscoveryRelation } from "@/types/paper";

export interface ProvenanceParent {
  /** PaperChase Paper id (PaperWithRole.id) of the paper we expanded from — NOT the S2 id. */
  paperId: string;
  method: Extract<DiscoveryMethod, "trawl" | "snowball">;
}

const MAX_LINKS = 20; // mirrors backend services/discovery_provenance.MAX_LINKS
const key = (l: DiscoveryLink) => `${l.parent_paper_id}|${l.relation}`;

export function tagLinks<T extends { discovered_via?: DiscoveryLink[] }>(
  papers: T[],
  parent: ProvenanceParent,
  relation: DiscoveryRelation,
): T[] {
  return papers.map((p) => ({
    ...p,
    discovered_via: [{ parent_paper_id: parent.paperId, relation, method: parent.method }],
  }));
}

/** Union by parent+relation, first occurrence wins, capped at MAX_LINKS. */
export function mergeLinks(existing: DiscoveryLink[], incoming: DiscoveryLink[]): DiscoveryLink[] {
  const out: DiscoveryLink[] = [];
  const seen = new Set<string>();
  for (const l of [...existing, ...incoming]) {
    if (out.length >= MAX_LINKS) break;
    if (seen.has(key(l))) continue;
    seen.add(key(l));
    out.push(l);
  }
  return out;
}

/** Distinct parents that are CURRENT seeds (seeds can change after trawl). */
export function seedParentIds(
  links: DiscoveryLink[] | null | undefined,
  seedIds: Set<string>,
): string[] {
  if (!links) return [];
  return [...new Set(links.map((l) => l.parent_paper_id))].filter((id) => seedIds.has(id));
}

const RELATION_TEXT: Record<DiscoveryRelation, string> = {
  cites_parent: "Cites",
  cited_by_parent: "Cited by",
  recommended_with: "Recommended with",
  cocited_with: "Co-cited with",
};

export function describeRelation(relation: DiscoveryRelation): string {
  return RELATION_TEXT[relation];
}
